import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-primary text-white p-12 relative overflow-hidden">
        <div className="flex items-center gap-2.5 relative z-10">
          <div className="h-9 w-9 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center">
            <Sparkles className="h-5 w-5" strokeWidth={2.2} />
          </div>
          <span className="font-display font-bold tracking-tight">TSG AI Hub</span>
        </div>
        <div className="relative z-10">
          <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-4">Tai Sang Global</p>
          <h1 className="font-display text-5xl font-bold tracking-tighter leading-none">
            No enquiry<br />ever forgotten.
          </h1>
          <p className="text-white/60 mt-6 max-w-sm leading-relaxed">
            Upload a screenshot. AI reads the conversation, extracts the customer, and drafts your
            follow-up. Save a lead in under 30 seconds.
          </p>
        </div>
        <div className="text-white/40 text-xs relative z-10">AI-Powered Lead Management · Version 1</div>
        <div className="absolute -bottom-40 -right-40 h-96 w-96 rounded-full bg-white/5" />
        <div className="absolute -top-20 -left-20 h-72 w-72 rounded-full bg-white/5" />
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2.5 mb-4">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <span className="font-display font-bold tracking-tight">TSG AI Hub</span>
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight">Welcome back</h2>
            <p className="text-sm text-muted-foreground mt-1">Sign in to your workspace</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                placeholder="you@taisangglobal.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full h-11 font-semibold">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </Button>

          <div className="text-xs text-muted-foreground border border-border rounded-md p-3 leading-relaxed">
            <span className="font-semibold text-foreground">Demo accounts</span> · password{" "}
            <code className="bg-secondary px-1 py-0.5 rounded">TSG@2026</code>
            <br />Admin: dashan@taisangglobal.com · Consultant: gulab@taisangglobal.com
          </div>
        </form>
      </div>
    </div>
  );
}
